/**
 * WebRTC Hook - Enhanced peer connections for video/voice calls
 * Improvements: Better ICE handling, connection recovery, faster polling
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { API_BASE } from '../config/api';

// Multiple STUN/TURN servers for better connectivity
const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
  { urls: 'stun:stun3.l.google.com:19302' },
  { urls: 'stun:stun4.l.google.com:19302' },
  // OpenRelay free TURN servers
  {
    urls: 'turn:openrelay.metered.ca:80',
    username: 'openrelayproject',
    credential: 'openrelayproject',
  },
  {
    urls: 'turn:openrelay.metered.ca:443',
    username: 'openrelayproject',
    credential: 'openrelayproject',
  },
  {
    urls: 'turn:openrelay.metered.ca:443?transport=tcp',
    username: 'openrelayproject',
    credential: 'openrelayproject',
  },
];

// Polling intervals
const SIGNAL_POLL_INTERVAL = 500; // Faster polling for quicker connections
const PARTICIPANT_SYNC_INTERVAL = 5000; // Check for new participants
const CONNECTION_RETRY_DELAY = 3000;
const MAX_RETRY_ATTEMPTS = 3;

export interface Peer {
  odUserId: string;
  connection: RTCPeerConnection;
  stream: MediaStream | null;
  dataChannel: RTCDataChannel | null;
  retryCount: number;
  lastActivity: number;
}

export function useWebRTC(
  roomCode: string,
  userId: string,
  onRemoteStream?: (odUserId: string, stream: MediaStream) => void,
  onPeerDisconnect?: (odUserId: string) => void
) {
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [connectionState, setConnectionState] = useState<'idle' | 'connecting' | 'connected' | 'failed'>('idle');
  const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(new Map());

  const peersRef = useRef<Map<string, Peer>>(new Map());
  const localStreamRef = useRef<MediaStream | null>(null);
  const pollingRef = useRef<number | null>(null);
  const participantSyncRef = useRef<number | null>(null);
  const lastSignalTimeRef = useRef<number>(0);
  const pendingCandidatesRef = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());
  const isJoinedRef = useRef(false);

  // Start local media (camera/microphone)
  const startMedia = useCallback(async (callType: 'video' | 'voice' = 'video') => {
    try {
      setConnectionState('connecting');

      const constraints: MediaStreamConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: callType === 'video' ? {
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          frameRate: { ideal: 30, max: 60 },
          facingMode: 'user',
        } : false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setLocalStream(stream);
      localStreamRef.current = stream;
      setIsVideoEnabled(callType === 'video');
      setIsAudioEnabled(true);

      console.log('[WebRTC] Local media started:', stream.getTracks().map(t => t.kind));
      return stream;
    } catch (err) {
      console.error('[WebRTC] Failed to get media:', err);
      // Don't set failed state - allow joining as viewer
      setConnectionState('connecting');
      throw err;
    }
  }, []);

  // Stop local media
  const stopMedia = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
      setLocalStream(null);
    }
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
      setScreenStream(null);
      setIsScreenSharing(false);
    }
  }, [screenStream]);

  // Send a signal through the signaling server
  const sendSignal = useCallback(async (toUserId: string, signalType: string, signalData: any) => {
    try {
      const response = await fetch(`${API_BASE}/meetings/${roomCode}/signal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromUserId: userId,
          toUserId,
          signalType,
          signalData,
        }),
      });
      if (!response.ok) {
        console.warn('[WebRTC] Signal send failed:', response.status);
      }
    } catch (err) {
      console.error('[WebRTC] Failed to send signal:', err);
    }
  }, [roomCode, userId]);

  // Add pending ICE candidates once remote description is set
  const flushPendingCandidates = useCallback(async (peerId: string, pc: RTCPeerConnection) => {
    const pending = pendingCandidatesRef.current.get(peerId);
    if (pending && pending.length > 0) {
      console.log(`[WebRTC] Flushing ${pending.length} pending ICE candidates for ${peerId}`);
      for (const candidate of pending) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (err) {
          console.warn('[WebRTC] Failed to add pending ICE candidate:', err);
        }
      }
      pendingCandidatesRef.current.set(peerId, []);
    }
  }, []);

  // Create a peer connection with enhanced error handling
  const createPeerConnection = useCallback((remoteUserId: string, isInitiator: boolean = true): RTCPeerConnection => {
    console.log('[WebRTC] Creating peer connection for:', remoteUserId, 'initiator:', isInitiator);

    const pc = new RTCPeerConnection({
      iceServers: ICE_SERVERS,
      iceCandidatePoolSize: 10,
    });

    // Track if we're in the middle of creating an offer
    let isNegotiating = false;
    let makingOffer = false;

    // Add local tracks to the connection
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        console.log('[WebRTC] Adding local track:', track.kind);
        pc.addTrack(track, localStreamRef.current!);
      });
    } else {
      // Create empty transceivers so we can receive tracks even without local media
      pc.addTransceiver('audio', { direction: 'recvonly' });
      pc.addTransceiver('video', { direction: 'recvonly' });
    }

    // Handle incoming tracks
    pc.ontrack = (event) => {
      console.log('[WebRTC] Received track from', remoteUserId, ':', event.track.kind);
      const [remoteStream] = event.streams;
      if (remoteStream) {
        // Update peer's stream
        const peer = peersRef.current.get(remoteUserId);
        if (peer) {
          peer.stream = remoteStream;
          peer.lastActivity = Date.now();
        }

        setRemoteStreams(prev => {
          const next = new Map(prev);
          next.set(remoteUserId, remoteStream);
          return next;
        });
        onRemoteStream?.(remoteUserId, remoteStream);
        setConnectionState('connected');
      }
    };

    // Handle ICE candidates - send immediately (trickle ICE)
    pc.onicecandidate = async (event) => {
      if (event.candidate) {
        console.log('[WebRTC] Sending ICE candidate to', remoteUserId);
        await sendSignal(remoteUserId, 'ice-candidate', event.candidate.toJSON());
      }
    };

    // Log ICE gathering state
    pc.onicegatheringstatechange = () => {
      console.log(`[WebRTC] ICE gathering state (${remoteUserId}):`, pc.iceGatheringState);
    };

    // Handle ICE connection state
    pc.oniceconnectionstatechange = () => {
      console.log(`[WebRTC] ICE connection state (${remoteUserId}):`, pc.iceConnectionState);

      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        setConnectionState('connected');
        const peer = peersRef.current.get(remoteUserId);
        if (peer) {
          peer.retryCount = 0;
          peer.lastActivity = Date.now();
        }
      } else if (pc.iceConnectionState === 'failed') {
        // Try ICE restart
        console.log('[WebRTC] ICE failed, attempting restart for', remoteUserId);
        handleConnectionFailure(remoteUserId);
      } else if (pc.iceConnectionState === 'disconnected') {
        // Wait a bit then check if still disconnected
        setTimeout(() => {
          if (pc.iceConnectionState === 'disconnected') {
            console.log('[WebRTC] Still disconnected, retrying', remoteUserId);
            handleConnectionFailure(remoteUserId);
          }
        }, 3000);
      }
    };

    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      console.log(`[WebRTC] Connection state (${remoteUserId}):`, pc.connectionState);

      if (pc.connectionState === 'connected') {
        setConnectionState('connected');
      } else if (pc.connectionState === 'failed') {
        handleConnectionFailure(remoteUserId);
      } else if (pc.connectionState === 'closed') {
        cleanupPeer(remoteUserId);
      }
    };

    // Handle negotiation needed (for renegotiation after track changes)
    // IMPORTANT: Ignore during initial connection - we handle that in connectToPeer
    pc.onnegotiationneeded = async () => {
      // Skip if we're already negotiating or if this is the initial connection
      if (makingOffer || isNegotiating) {
        console.log('[WebRTC] Skipping negotiation - already in progress for', remoteUserId);
        return;
      }

      // Only renegotiate if connection is already established
      if (pc.connectionState !== 'connected') {
        console.log('[WebRTC] Skipping negotiation - not yet connected for', remoteUserId);
        return;
      }

      console.log('[WebRTC] Renegotiation needed for', remoteUserId);
      const peer = peersRef.current.get(remoteUserId);
      if (peer && pc.signalingState === 'stable') {
        try {
          makingOffer = true;
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          await sendSignal(remoteUserId, 'offer', pc.localDescription);
        } catch (err) {
          console.error('[WebRTC] Renegotiation failed:', err);
        } finally {
          makingOffer = false;
        }
      }
    };

    // Store the negotiation flag setter on the peer connection for external use
    (pc as any)._setNegotiating = (value: boolean) => { isNegotiating = value; };
    (pc as any)._setMakingOffer = (value: boolean) => { makingOffer = value; };

    return pc;
  }, [onRemoteStream, sendSignal]);

  // Handle connection failure with retry logic
  const handleConnectionFailure = useCallback(async (remoteUserId: string) => {
    const peer = peersRef.current.get(remoteUserId);
    if (!peer) return;

    peer.retryCount = (peer.retryCount || 0) + 1;

    if (peer.retryCount <= MAX_RETRY_ATTEMPTS) {
      console.log(`[WebRTC] Retry attempt ${peer.retryCount}/${MAX_RETRY_ATTEMPTS} for ${remoteUserId}`);

      // Close existing connection
      peer.connection.close();

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, CONNECTION_RETRY_DELAY));

      // Create new connection
      const newPc = createPeerConnection(remoteUserId);
      peer.connection = newPc;
      peer.lastActivity = Date.now();

      try {
        const offer = await newPc.createOffer({ iceRestart: true });
        await newPc.setLocalDescription(offer);
        await sendSignal(remoteUserId, 'offer', offer);
      } catch (err) {
        console.error('[WebRTC] Retry offer failed:', err);
      }
    } else {
      console.log('[WebRTC] Max retries exceeded for', remoteUserId);
      cleanupPeer(remoteUserId);
    }
  }, [createPeerConnection, sendSignal]);

  // Clean up a peer connection
  const cleanupPeer = useCallback((remoteUserId: string) => {
    const peer = peersRef.current.get(remoteUserId);
    if (peer) {
      peer.connection.close();
      peersRef.current.delete(remoteUserId);
      setRemoteStreams(prev => {
        const next = new Map(prev);
        next.delete(remoteUserId);
        return next;
      });
      onPeerDisconnect?.(remoteUserId);
    }
    pendingCandidatesRef.current.delete(remoteUserId);
  }, [onPeerDisconnect]);

  // Create offer and initiate connection
  const connectToPeer = useCallback(async (remoteUserId: string) => {
    if (remoteUserId === userId) return; // Don't connect to self

    const existingPeer = peersRef.current.get(remoteUserId);
    if (existingPeer) {
      const state = existingPeer.connection.connectionState;
      if (state === 'connected' || state === 'connecting') {
        console.log('[WebRTC] Already connected/connecting to', remoteUserId);
        return;
      }
      // Clean up stale connection
      existingPeer.connection.close();
    }

    console.log('[WebRTC] Creating offer for', remoteUserId);
    const pc = createPeerConnection(remoteUserId, true);

    // Mark as negotiating to prevent onnegotiationneeded from firing
    if ((pc as any)._setNegotiating) (pc as any)._setNegotiating(true);
    if ((pc as any)._setMakingOffer) (pc as any)._setMakingOffer(true);

    peersRef.current.set(remoteUserId, {
      odUserId: remoteUserId,
      connection: pc,
      stream: null,
      dataChannel: null,
      retryCount: 0,
      lastActivity: Date.now(),
    });

    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await sendSignal(remoteUserId, 'offer', offer);
      console.log('[WebRTC] Offer sent to', remoteUserId);
    } catch (err) {
      console.error('[WebRTC] Failed to create offer:', err);
      cleanupPeer(remoteUserId);
    } finally {
      // Reset negotiating flags
      if ((pc as any)._setNegotiating) (pc as any)._setNegotiating(false);
      if ((pc as any)._setMakingOffer) (pc as any)._setMakingOffer(false);
    }
  }, [userId, createPeerConnection, sendSignal, cleanupPeer]);

  // Handle incoming signal
  const handleSignal = useCallback(async (fromUserId: string, signalType: string, signalData: any) => {
    if (fromUserId === userId) return; // Ignore signals from self

    console.log(`[WebRTC] Received ${signalType} from ${fromUserId}`);

    let peer = peersRef.current.get(fromUserId);

    if (signalType === 'offer') {
      // Create peer connection if doesn't exist
      if (peer) {
        // Handle offer collision - if we're the "polite" peer, accept the offer
        if (peer.connection.signalingState !== 'stable') {
          console.log('[WebRTC] Offer collision, rolling back');
          await peer.connection.setLocalDescription({ type: 'rollback' });
        }
      } else {
        const pc = createPeerConnection(fromUserId);
        peer = {
          odUserId: fromUserId,
          connection: pc,
          stream: null,
          dataChannel: null,
          retryCount: 0,
          lastActivity: Date.now(),
        };
        peersRef.current.set(fromUserId, peer);
      }

      try {
        await peer.connection.setRemoteDescription(new RTCSessionDescription(signalData));
        await flushPendingCandidates(fromUserId, peer.connection);

        const answer = await peer.connection.createAnswer();
        await peer.connection.setLocalDescription(answer);
        await sendSignal(fromUserId, 'answer', answer);
        console.log('[WebRTC] Answer sent to', fromUserId);
      } catch (err) {
        console.error('[WebRTC] Failed to handle offer:', err);
      }

    } else if (signalType === 'answer') {
      if (peer && peer.connection.signalingState === 'have-local-offer') {
        try {
          await peer.connection.setRemoteDescription(new RTCSessionDescription(signalData));
          await flushPendingCandidates(fromUserId, peer.connection);
          console.log('[WebRTC] Answer processed from', fromUserId);
        } catch (err) {
          console.error('[WebRTC] Failed to handle answer:', err);
        }
      }

    } else if (signalType === 'ice-candidate') {
      if (peer && peer.connection.remoteDescription) {
        try {
          await peer.connection.addIceCandidate(new RTCIceCandidate(signalData));
        } catch (err) {
          console.warn('[WebRTC] Failed to add ICE candidate:', err);
        }
      } else {
        // Queue the candidate until remote description is set
        console.log('[WebRTC] Queueing ICE candidate for', fromUserId);
        const pending = pendingCandidatesRef.current.get(fromUserId) || [];
        pending.push(signalData);
        pendingCandidatesRef.current.set(fromUserId, pending);
      }
    }
  }, [userId, createPeerConnection, sendSignal, flushPendingCandidates]);

  // Poll for signals from the server
  const startSignalPolling = useCallback(() => {
    if (pollingRef.current) return;
    isJoinedRef.current = true;

    console.log('[WebRTC] Starting signal polling');

    const poll = async () => {
      if (!isJoinedRef.current) return;

      try {
        const response = await fetch(
          `${API_BASE}/meetings/${roomCode}/signals?userId=${userId}&since=${lastSignalTimeRef.current}`
        );

        if (response.ok) {
          const data = await response.json();

          if (data.signals && data.signals.length > 0) {
            console.log(`[WebRTC] Processing ${data.signals.length} signals`);
            for (const signal of data.signals) {
              await handleSignal(signal.from_user_id, signal.signal_type, signal.signalData);
              lastSignalTimeRef.current = Math.max(lastSignalTimeRef.current, signal.created_at);
            }
          }
        }
      } catch (err) {
        console.error('[WebRTC] Signal polling error:', err);
      }

      if (isJoinedRef.current) {
        pollingRef.current = window.setTimeout(poll, SIGNAL_POLL_INTERVAL);
      }
    };

    poll();
  }, [roomCode, userId, handleSignal]);

  // Periodically sync participants to catch missed connections
  const startParticipantSync = useCallback(() => {
    if (participantSyncRef.current) return;

    console.log('[WebRTC] Starting participant sync');

    const sync = async () => {
      if (!isJoinedRef.current) return;

      try {
        const response = await fetch(`${API_BASE}/meetings/${roomCode}/participants`);
        if (response.ok) {
          const data = await response.json();
          const participants = data.participants || [];

          // Connect to any participants we're not connected to
          for (const p of participants) {
            // Skip self
            if (p.odUserId === userId) continue;

            const existingPeer = peersRef.current.get(p.odUserId);
            if (!existingPeer) {
              console.log('[WebRTC] Sync: connecting to missed participant', p.odUserId);
              connectToPeer(p.odUserId);
            } else {
              // Only reconnect if connection is truly failed (not just connecting)
              const state = existingPeer.connection.connectionState;
              if (state === 'failed' || state === 'closed') {
                console.log('[WebRTC] Sync: reconnecting to failed participant', p.odUserId);
                cleanupPeer(p.odUserId);
                connectToPeer(p.odUserId);
              }
            }
          }

          // Clean up peers that are no longer in the participant list
          // BUT only if they've been missing for a while (to avoid race conditions)
          const activeIds = new Set(participants.map((p: any) => p.odUserId));
          peersRef.current.forEach((peer, peerId) => {
            if (!activeIds.has(peerId)) {
              // Check if this peer has been inactive for at least 10 seconds
              const inactiveTime = Date.now() - peer.lastActivity;
              if (inactiveTime > 10000) {
                console.log('[WebRTC] Sync: cleaning up departed participant', peerId);
                cleanupPeer(peerId);
              }
            }
          });
        }
      } catch (err) {
        console.error('[WebRTC] Participant sync error:', err);
      }

      if (isJoinedRef.current) {
        participantSyncRef.current = window.setTimeout(sync, PARTICIPANT_SYNC_INTERVAL);
      }
    };

    // Start sync after a longer delay to let initial connections establish
    setTimeout(sync, 5000);
  }, [roomCode, userId, connectToPeer, cleanupPeer]);

  const stopSignalPolling = useCallback(() => {
    isJoinedRef.current = false;
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    if (participantSyncRef.current) {
      clearTimeout(participantSyncRef.current);
      participantSyncRef.current = null;
    }
  }, []);

  // Toggle audio
  const toggleAudio = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsAudioEnabled(prev => !prev);
    }
  }, []);

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsVideoEnabled(prev => !prev);
    }
  }, []);

  // Start screen sharing
  const startScreenShare = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 },
        } as any,
        audio: true, // Include system audio if available
      });

      setScreenStream(stream);
      setIsScreenSharing(true);

      // Replace video track in all peer connections
      const videoTrack = stream.getVideoTracks()[0];
      const audioTrack = stream.getAudioTracks()[0];

      console.log('[WebRTC] Screen share started, updating peers');

      peersRef.current.forEach(peer => {
        // Replace video track
        const videoSender = peer.connection.getSenders().find(s => s.track?.kind === 'video');
        if (videoSender) {
          videoSender.replaceTrack(videoTrack);
        } else {
          peer.connection.addTrack(videoTrack, stream);
        }

        // Add audio track if available
        if (audioTrack) {
          peer.connection.addTrack(audioTrack, stream);
        }
      });

      // Handle when user stops sharing via browser UI
      videoTrack.onended = () => {
        stopScreenShare();
      };

      return stream;
    } catch (err) {
      console.warn('[WebRTC] Screen share cancelled or failed:', err);
      return null;
    }
  }, []);

  // Stop screen sharing
  const stopScreenShare = useCallback(() => {
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
      setScreenStream(null);
      setIsScreenSharing(false);

      // Restore camera video track in all peer connections
      if (localStreamRef.current) {
        const videoTrack = localStreamRef.current.getVideoTracks()[0];
        if (videoTrack) {
          peersRef.current.forEach(peer => {
            const sender = peer.connection.getSenders().find(s => s.track?.kind === 'video');
            if (sender) {
              sender.replaceTrack(videoTrack);
            }
          });
        }
      }
    }
  }, [screenStream]);

  // Disconnect from all peers
  const disconnectAll = useCallback(() => {
    console.log('[WebRTC] Disconnecting all');
    stopSignalPolling();
    stopMedia();

    peersRef.current.forEach(peer => {
      peer.connection.close();
    });
    peersRef.current.clear();
    pendingCandidatesRef.current.clear();
    setRemoteStreams(new Map());
    setConnectionState('idle');
    lastSignalTimeRef.current = 0;
  }, [stopSignalPolling, stopMedia]);

  // Start participant sync when signal polling starts
  useEffect(() => {
    if (isJoinedRef.current) {
      startParticipantSync();
    }
  }, [startParticipantSync]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectAll();
    };
  }, []);

  return {
    // State
    localStream,
    screenStream,
    remoteStreams,
    isAudioEnabled,
    isVideoEnabled,
    isScreenSharing,
    connectionState,
    peers: peersRef,

    // Actions
    startMedia,
    stopMedia,
    connectToPeer,
    handleSignal,
    toggleAudio,
    toggleVideo,
    startScreenShare,
    stopScreenShare,
    startSignalPolling,
    stopSignalPolling,
    disconnectAll,
  };
}
